import React, { useEffect, useMemo, useContext, useState, useRef } from 'react';
import PropTypes from 'prop-types';

import {
    assignPositions,
    composeColumnData,
    composeRowData,
    getLeaderboardWithAllMatches
} from './utils';
import { useDispatch, useSelector } from 'react-redux';
import {
    leaderboardFavouriteSelector,
    leaderboardFilterSelector
} from 'common/store/modules/player-awards/leaderboard/selectors';
import FocusableTooltipArea from 'common/react/components/tooltip-button/focusable-tooltip/FocusableTootlipArea';
import { Table } from './components/Table';
import {
    useGetBrownlowPredictorQuery,
    useGetMatchesQuery,
    useGetTeamsQuery
} from 'common/store/modules/api/afl-api';
import { SeasonContext } from './LeaderboardTableFetcher';
import { sortFavourites } from 'widgets/brownlow-predictor-v2/js/utils/utils';
import { TooltipContent } from '../../../Tooltip';
import { EmptyState } from 'common/react/components/EmptyState';
import { LeaderboardTableSkeleton } from '../skeleton-loaders/LeaderboardTableSkeleton';
import { useCurrentPlayerPulseIds } from '../../hooks/use-current-player-pulse-ids';
import { useGetCurrentRoundNumber } from '../../../hooks/use-find-current-round';
import { navSelector } from 'common/store/modules/competition-nav/selectors';
import { leaderboardSelector } from 'common/store/modules/player-awards/leaderboard/selectors/simple-selectors';

import {
    setFavouritePlayer,
    setLeaderboardStateItem
} from 'common/store/modules/player-awards/leaderboard';

export const LeaderboardTable = ({
    shouldHidePredicted,
    leaderboardData,
    fetchMoreData,
    setData,
    shownData,
    filteredData,
    pageSize,
    currentPage,
    shouldShowBettingOdds,
    bettingOddsData,
    showLeader,
    title,
    isLeaderboardFetching
}) => {
    const dispatch = useDispatch();

    const [currentPlayerIds, setCurrentPlayerIds] = useState([]);

    const nav = useSelector(navSelector);
    const leaderboardState = useSelector(leaderboardSelector);

    const isAFLW = PULSE.app.common.url.checkBaseRoute('aflw');
    const showPredictedData = !isAFLW && leaderboardState?.showPredicted;

    const season = useContext(SeasonContext);
    const seasonPid = season?.providerId ?? '';

    const rounds = nav?.rounds;

    const { data: teamsData } = useGetTeamsQuery(nav.season.id, {
        skip: !nav.season.id ? true : false
    });
    const teams = teamsData || [];

    const team = teams.find((item) => item.id === leaderboardState.team.id);
    const teamId = team?.providerId ?? '';

    const params = {
        competition: nav.competition.id,
        compSeasonId: nav.season.id,
        pageSize: 250
    };

    const { data: allSeasonMatches = [], isFetching: isMatchesFetching } =
        useGetMatchesQuery(params, {
            skip: !params.competition || !params.compSeasonId
        });

    const favourites = useSelector(leaderboardFavouriteSelector);
    const leaderboardFilter = useSelector(leaderboardFilterSelector);

    /**
     * On Player Favourite Click
     * Modify favourites on player add/remove
     *
     * @param {string} playerId
     */
    const onPlayerFavouriteClick = (playerId) => {
        dispatch(
            setFavouritePlayer({
                id: null,
                providerId: playerId
            })
        );
    };

    const onSortByFavouriteClick = (favourite) => {
        dispatch(
            setLeaderboardStateItem({
                item: 'sortByFavourites',
                value: favourite
            })
        );
    };

    const leaderboardWithAllMatches = useMemo(() => {
        if (
            leaderboardData?.leaderboard.length &&
            allSeasonMatches?.length &&
            rounds
        ) {
            return getLeaderboardWithAllMatches(
                leaderboardData.leaderboard,
                allSeasonMatches,
                rounds
            );
        }

        return [];
    }, [rounds, leaderboardData, allSeasonMatches]);

    const currentRoundNumber = useGetCurrentRoundNumber(seasonPid);

    useEffect(() => {
        if (leaderboardWithAllMatches.length) {
            let items = leaderboardWithAllMatches.map((item) => ({
                eligible: item.eligible ?? false,
                player: {
                    ...item.player,
                    leader: item.leader ?? false,
                    winner: item.winner ?? false
                },
                roundByRoundVotes: item.roundByRoundVotes ?? [],
                roundByRoundTotalVotes: item.roundByRoundTotalVotes ?? [],
                team: { ...item.team },
                totalVotes: item.totalVotes ?? '',
                bettingOdds: shouldShowBettingOdds
                    ? bettingOddsData?.outcomes?.find(
                          (outcome) => item.player.playerId === outcome.playerId
                      )?.price ?? null
                    : null
            }));
            items = assignPositions(items);
            if (favourites.length && leaderboardState.sortByFavourites) {
                items = sortFavourites(items, favourites, teamId);
            }
            setData(items);
        }
    }, [
        leaderboardWithAllMatches,
        favourites,
        leaderboardState?.sortByFavourites,
        teamId,
        bettingOddsData?.outcomes,
        shouldShowBettingOdds
    ]);

    /**
     * Get predictor data
     */
    const { data: predicted } = useGetBrownlowPredictorQuery(
        {
            compSeasonId: season?.id,
            players: currentPlayerIds?.join(',')
        },
        {
            skip: !(currentPlayerIds.length && season?.id)
        }
    );

    /**
     * Get columns
     */
    const columns = useMemo(() => composeColumnData(rounds), [rounds]);

    /**
     * Get predicted data for currently visible players
     */
    const [predictedPlayers, setPredictedPlayers] = useState({
        results: [],
        newItems: []
    });
    const lastProcessedPredictedPlayers = useRef(null);

    useEffect(() => {
        if (predicted && predicted !== lastProcessedPredictedPlayers.current) {
            setPredictedPlayers((current) => {
                return {
                    results: [...current.results, ...predicted.results],
                    newItems: predicted.newItems
                };
            });

            lastProcessedPredictedPlayers.current = predicted;
        }
    }, [predicted]);

    /**
     * Get rows
     */
    const rowData = useMemo(
        () =>
            shownData?.map((item) =>
                composeRowData(
                    item,
                    columns.filter(
                        (col) =>
                            !['player', 'totalVotes', 'bettingOdds'].includes(
                                col.accessor
                            )
                    ),
                    rounds,
                    false,
                    predictedPlayers?.results
                )
            ),
        [columns, shownData, rounds, predictedPlayers?.results]
    );

    // Handles retrieving current player pulse IDs
    useCurrentPlayerPulseIds({
        rowData,
        currentPage,
        pageSize,
        currentPlayerIds,
        setCurrentPlayerIds
    });

    if (isMatchesFetching || isLeaderboardFetching) {
        return <LeaderboardTableSkeleton />;
    }

    const leaderBoardHasData =
        leaderboardData && leaderboardData.leaderboard?.length > 0 && shownData;

    const isFiltered = leaderboardFilter?.query?.length > 0;

    if (!leaderBoardHasData && !isFiltered) {
        return (
            <div className="career-and-season-stats__empty-state">
                <EmptyState
                    titleTranslation="label.brownlow.tracker.error.noDataAvailable"
                    summaryTranslation="label.brownlow.tracker.error.noDataAvailable.summary"
                />
            </div>
        );
    }

    return (
        <FocusableTooltipArea>
            <Table
                key={rowData[0]?.player.playerId}
                columns={columns}
                data={rowData}
                fullData={filteredData}
                modifier={`player-awards-tracker-leaderboard ${
                    shouldShowBettingOdds
                        ? 'stats-table--player-awards-tracker-leaderboard--show-odds'
                        : ''
                }`}
                update={fetchMoreData}
                activeColumn={currentRoundNumber}
                seasonPid={seasonPid}
                showLeader={showLeader}
                favourites={favourites}
                setSortByFavourites={onSortByFavouriteClick}
                sortByFavourites={leaderboardState?.sortByFavourites}
                onPlayerFavouriteClick={onPlayerFavouriteClick}
                filteredResults={leaderboardFilter?.query?.length > 0}
                showPredictedData={showPredictedData}
                isBrownlowTracker={true}
                shouldHidePredicted={shouldHidePredicted}
                primaryTooltipComponent={(row, cell) => (
                    <TooltipContent
                        row={row.original}
                        cell={cell}
                        player={row.original?.player.playerId}
                        seasonPid={seasonPid}
                        leaderboardData={leaderboardData}
                    />
                )}
                title={title}
                status={leaderboardData?.status ?? null}
            />
        </FocusableTooltipArea>
    );
};

LeaderboardTable.propTypes = {
    fetchMoreData: PropTypes.func,
    setData: PropTypes.func,
    leaderboardData: PropTypes.object,
    filteredData: PropTypes.array,
    pageSize: PropTypes.number,
    currentPage: PropTypes.number,
    shownData: PropTypes.array,
    shouldShowBettingOdds: PropTypes.bool,
    showLeader: PropTypes.bool,
    shouldHidePredicted: PropTypes.bool,
    bettingOddsData: PropTypes.object,
    title: PropTypes.string,
    isLeaderboardFetching: PropTypes.bool,
    activeColumn: PropTypes.number
};
